import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { Agent, AgentFetchResult } from "../types";
import { extractErrorMessageFromResponse, isTemplateAgentId } from "../utils";

/**
 * Suspended atom family that fetches a single Agent.
 *
 * Returns:
 *   • `{ type: "success", data }` – ok
 *   • `{ type: "forbidden" }`     – 403
 *   • `{ type: "not_found" }`     – 404
 *   • `null`                      – any other error (400, 5xx, network)
 *   • `undefined`                 – no `agentId` provided **or** a ZSL-agent
 */
export const fetchAgentAtomFamily_SUSPENDS = atomFamily(
  (agentId: string | undefined) => {
    const agentAtom = atom(
      async (): Promise<AgentFetchResult | null | undefined> => {
        if (!agentId) {
          return;
        }

        const isTemplateAgent = isTemplateAgentId(agentId);

        // Because we don't want to fetch with template asset id and get a 400 Bad Request error
        if (isTemplateAgent) {
          return;
        }

        try {
          const res = await fetch(`/api/canvasui/agents/${agentId}`);

          if (res.ok) {
            const agent = (await res.json()) as Agent;
            return { type: "success", data: agent };
          }

          /* ----- expected error codes we surface to UI ----- */
          if (res.status === 404) {
            return { type: "not_found" };
          }
          if (res.status === 403) {
            return { type: "forbidden" };
          }

          /* ----- 5xx or other unexpected errors – log & degrade ----- */
          const errorMessage = await extractErrorMessageFromResponse(res);
          console.error(`Failed to fetch agent ${agentId}: ${errorMessage}`);
          return null;
        } catch (error) {
          console.error("Network error fetching agent:", error);
          // Network/unknown error – return null so UI can degrade gracefully
          return null;
        }
      },
    );

    agentAtom.debugLabel = `fetchAgentAtomFamily_SUSPENDS(${agentId})`;
    return agentAtom;
  },
);
